// ===========================================
// COACH STORE
// ===========================================
// Stores coaching messages for the COACH chat tab.
// Messages are generated by the recommendation engine.
// ===========================================

import { create } from 'zustand';

// ===========================================
// TYPES
// ===========================================

export interface CoachMessage {
  id: string;
  timestamp: number;
  type: 'recommendation' | 'insurance' | 'deviation' | 'info';
  message: string;
  /** Additional context (e.g., true count, reason) */
  details?: string;
  /** Outcome of following this recommendation: good (✓), bad (✗), neutral (-), or pending (null) */
  outcome?: 'good' | 'bad' | 'neutral' | null;
  /** The action that was recommended */
  recommendedAction?: string;
  /** Whether the player ignored this recommendation and took a different action */
  ignored?: boolean;
}

interface CoachState {
  messages: CoachMessage[];
  /** Whether the coach tab is enabled (mirrors showRecommendations) */
  enabled: boolean;
}

interface CoachActions {
  /** Add a new coach message, returns the message ID */
  addMessage: (type: CoachMessage['type'], message: string, details?: string, recommendedAction?: string) => string;
  /** Update the outcome of a message (good = ✓, bad = ✗, neutral = -) */
  updateMessageOutcome: (messageId: string, outcome: 'good' | 'bad' | 'neutral') => void;
  /** Mark a message as ignored (player took different action) */
  markMessageIgnored: (messageId: string) => void;
  /** Clear all messages (e.g., on table leave) */
  clearMessages: () => void;
  /** Set enabled state */
  setEnabled: (enabled: boolean) => void;
}

type CoachStore = CoachState & CoachActions;

// ===========================================
// STORE
// ===========================================

export const useCoachStore = create<CoachStore>((set, get) => ({
  messages: [],
  enabled: false,

  addMessage: (type, message, details, recommendedAction) => {
    const messageId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    // Only add if enabled
    if (!get().enabled) return messageId;

    const newMessage: CoachMessage = {
      id: messageId,
      timestamp: Date.now(),
      type,
      message,
      details,
      outcome: null, // Pending until we know the result
      recommendedAction,
      ignored: false,
    };

    set((state) => ({
      messages: [...state.messages.slice(-99), newMessage], // Keep last 100 messages
    }));

    return messageId;
  },

  updateMessageOutcome: (messageId, outcome) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === messageId ? { ...msg, outcome } : msg
      ),
    }));
  },

  markMessageIgnored: (messageId) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === messageId ? { ...msg, ignored: true } : msg
      ),
    }));
  },

  clearMessages: () => {
    set({ messages: [] });
  },

  setEnabled: (enabled) => {
    set({ enabled });
  },
}));

// ===========================================
// SELECTORS
// ===========================================

export const selectCoachMessages = (state: CoachStore) => state.messages;
export const selectCoachEnabled = (state: CoachStore) => state.enabled;

export default useCoachStore;
